import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../store'
import { breadcrumbRoutes, extractParams, BreadcrumbCrumbConfig } from '../utils/breadcrumbConfig'

/** A fully resolved breadcrumb crumb ready for rendering. */
export interface ResolvedCrumb {
  /** Display label (already translated or resolved from dynamic data) */
  label: string
  /** Navigation path — empty string means no link (current page) */
  path: string
  /** Router state to pass when navigating (preserves multi-tenant `env`) */
  navigationState: Record<string, unknown>
  /** Whether this crumb represents the current page (last in trail) */
  isCurrentPage: boolean
}

/** Return type of the `useBreadcrumbs` hook. */
export interface BreadcrumbState {
  /** Resolved breadcrumb trail */
  crumbs: ResolvedCrumb[]
  /** Whether breadcrumbs should be visible on the current page */
  visible: boolean
}

/**
 * Hook that resolves breadcrumb trail for the current route.
 *
 * Matches `location.pathname` against `breadcrumbConfig` patterns and resolves
 * dynamic labels (`$companyName`, `$meterLabel`) from `location.state` with
 * Redux fallbacks.
 *
 * @returns Breadcrumb state with resolved crumbs and visibility flag
 */
export const useBreadcrumbs = (): BreadcrumbState => {
  const location = useLocation()
  const { t } = useTranslation()
  const selectedCompany = useAppSelector(state => state.company?.selectedCompany)

  return useMemo(() => {
    const pathname = location.pathname
    const state = (location.state as Record<string, any>) || {}

    // Find matching route config
    const matchedRoute = breadcrumbRoutes.find(route => route.pattern.test(pathname))

    if (!matchedRoute) {
      return { crumbs: [], visible: false }
    }

    const params = extractParams(pathname, matchedRoute)
    const env = state.env || state.currentCompany?.__source_env || selectedCompany?.__source_env

    // Resolve each crumb
    const crumbs: ResolvedCrumb[] = matchedRoute.crumbs.map((crumbConfig, index) => {
      const isCurrentPage = index === matchedRoute.crumbs.length - 1

      // Resolve path
      let path = ''
      if (!isCurrentPage) {
        path = typeof crumbConfig.path === 'function' ? crumbConfig.path(params) : crumbConfig.path
      }

      // Resolve label
      const label = resolveCrumbLabel(crumbConfig, state, selectedCompany, t)

      return {
        label,
        path,
        navigationState: env ? { env } : {},
        isCurrentPage,
      }
    })

    return { crumbs, visible: true }
  }, [location.pathname, location.state, selectedCompany, t])
}

/**
 * Resolves the display label for a breadcrumb crumb.
 *
 * Dynamic labels (`$companyName`, `$meterLabel`) are resolved from
 * location.state first, with Redux state as fallback.
 */
function resolveCrumbLabel(
  config: BreadcrumbCrumbConfig,
  locationState: Record<string, any>,
  selectedCompany: any,
  t: (key: string) => string
): string {
  switch (config.labelKey) {
    case '$companyName': {
      const name =
        locationState.companyName ||
        locationState.currentCompany?.name ||
        locationState.currentCompany?.companyName ||
        selectedCompany?.name ||
        selectedCompany?.companyName
      return name || t('company.details')
    }
    case '$meterLabel': {
      const meter = locationState.meter
      if (meter?.serialNumber) return meter.serialNumber
      if (meter?.id) return `#${meter.id}`
      return t('company.meterDetails')
    }
    default:
      return t(config.labelKey)
  }
}
